import { Alert, Button, Group, Modal, Stack, Text, TextInput, toast } from '@common/ui'
import { useState } from 'react'
import { com } from '@eidu/web'
import { usePublishEntity } from '../../../../../io/useRegistryAsset'
import EntityId = com.eidu.entity.EntityId

type PublishDialogProps = {
  entityId: EntityId
  inlineDependencyCount?: number
}

const PublishDialog = ({ entityId, inlineDependencyCount }: PublishDialogProps) => {
  const [open, setOpen] = useState(false)
  const [minAppVersion, setMinAppVersion] = useState('')
  const { publish, isPending: publishing } = usePublishEntity()

  const onConfirm = async () => {
    try {
      const compatibility: Record<string, string> = minAppVersion.trim() ? { app_version: minAppVersion.trim() } : {}
      const result = await publish({ id: entityId, compatibility })
      toast.positive(`Published v${result.versionId.substring(0, 8)}…`)
      setOpen(false)
      setMinAppVersion('')
    } catch (error) {
      toast.negative(`Publish failed: ${(error as Error).message}`)
    }
  }

  return (
    <>
      <Button variant="subtle" onClick={() => setOpen(true)}>
        Publish
      </Button>
      <Modal opened={open} onClose={() => !publishing && setOpen(false)} title="Publish asset" size="lg">
        <Stack gap="md">
          <Text c="dimmed">
            Publishing creates an immutable snapshot of this asset and its dependencies, appended to the asset&apos;s
            versioned log. Field Apps with the right permission will pick up the new version on their next sync.
          </Text>
          {inlineDependencyCount !== undefined && inlineDependencyCount > 0 && (
            <Alert severity="caution">
              This asset has {inlineDependencyCount} non-published{' '}
              {inlineDependencyCount === 1 ? 'dependency' : 'dependencies'}. They will be embedded as-is and frozen at
              this version.
            </Alert>
          )}
          <TextInput
            label="Minimum app version (optional)"
            description="Field App versions older than this will ignore this publish."
            value={minAppVersion}
            onChange={(e) => setMinAppVersion(e.target.value)}
            placeholder="e.g. 3.15.0"
          />
          <Group justify="flex-end" mt="sm">
            <Button variant="default" onClick={() => setOpen(false)} disabled={publishing}>
              Cancel
            </Button>
            <Button onClick={onConfirm} disabled={publishing}>
              {publishing ? 'Publishing…' : 'Publish'}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  )
}

export default PublishDialog
